import { useTranslation } from "react-i18next"

import type { PersonalSourceView } from "@/api/client/personal-sources"

import {
  PERSONAL_SOURCE_KEYS,
  personalSourceUiState,
} from "@/features/personalization/components/personal-sources-utils"
import { cn } from "@/lib/utils"

type PersonalSearchSourceRowsProps = {
  /** The sources that would be searched (eligible and consented). */
  sources: readonly PersonalSourceView[]
  /** Personal content is already in this chat, so it cannot be shared. */
  tainted?: boolean
  /** Shown instead of the status rows when the switch is locked off. */
  lockedHint?: string
  className?: string
}

/**
 * Status line per source under the "Personal sources" switch: which sources the next question
 * will search, and a dot for the one whose Microsoft connection needs renewing (what was already
 * collected stays searchable until then). Read-only: managing sources lives in Settings.
 */
export function PersonalSearchSourceRows({
  sources,
  tainted = false,
  lockedHint,
  className,
}: PersonalSearchSourceRowsProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="personal-search-source-rows"
      className={cn(
        "text-muted-foreground flex flex-col gap-0.5 px-2 pt-0.5 pb-1.5 text-xs",
        className
      )}
    >
      {lockedHint ? (
        <p>{lockedHint}</p>
      ) : (
        <ul className="flex flex-col gap-0.5">
          {sources.map((view) => {
            const state = personalSourceUiState(view)
            const needsReconnect = state === "reconnect_required"
            return (
              <li
                key={view.source}
                className="flex items-center gap-1.5"
                data-source={view.source}
                data-state={state}
              >
                <span
                  aria-hidden
                  data-testid={needsReconnect ? "personal-search-reconnect-dot" : undefined}
                  className={cn(
                    "border-foreground size-1.5 shrink-0 rounded-full border",
                    needsReconnect ? "bg-warning-ink" : "bg-primary"
                  )}
                />
                <span className="min-w-0 truncate">
                  {t(PERSONAL_SOURCE_KEYS[view.source].title)}
                </span>
                {needsReconnect && (
                  <span className="text-warning-ink shrink-0">
                    {t("personalSources.search.reconnect", { defaultValue: "Reconnect needed" })}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {tainted && (
        <p data-testid="personal-search-tainted-note">
          {t("personalSources.search.taintedNote", {
            defaultValue: "This chat contains your personal content, so it cannot be shared.",
          })}
        </p>
      )}
    </div>
  )
}
